import { isNotificationSoundMuted } from "./notificationSoundPreference";

let audioContext: AudioContext | null = null;
let unlockListenersAttached = false;

const getAudioContext = (): AudioContext | null => {
	if (typeof window === "undefined") return null;
	const AudioCtx =
		window.AudioContext ||
		(window as Window & { webkitAudioContext?: typeof AudioContext })
			.webkitAudioContext;
	if (!AudioCtx) return null;
	if (!audioContext) audioContext = new AudioCtx();
	return audioContext;
};

const unlockAudioContext = () => {
	const ctx = getAudioContext();
	if (!ctx || ctx.state !== "suspended") return;
	void ctx.resume().catch(() => undefined);
};

/** Call once so browser autoplay policy allows notification sounds after user gesture. */
export const initMessageNotificationSound = (): void => {
	if (unlockListenersAttached || typeof document === "undefined") return;
	unlockListenersAttached = true;

	const unlock = () => {
		unlockAudioContext();
		document.removeEventListener("pointerdown", unlock);
		document.removeEventListener("keydown", unlock);
	};

	document.addEventListener("pointerdown", unlock, { passive: true });
	document.addEventListener("keydown", unlock, { passive: true });
};

/**
 * Short notification tone for a newly arrived message.
 * Only plays when the tab is visible (user is on the page right now).
 */
export const playMessageNotificationSound = (): void => {
	if (typeof document === "undefined") return;
	if (document.visibilityState !== "visible") return;
	if (isNotificationSoundMuted()) return;

	const ctx = getAudioContext();
	if (!ctx) return;

	const playTone = () => {
		const oscillator = ctx.createOscillator();
		const gain = ctx.createGain();

		oscillator.type = "sine";
		oscillator.frequency.setValueAtTime(880, ctx.currentTime);
		oscillator.frequency.exponentialRampToValueAtTime(
			660,
			ctx.currentTime + 0.12,
		);

		gain.gain.setValueAtTime(0.0001, ctx.currentTime);
		gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
		gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);

		oscillator.connect(gain);
		gain.connect(ctx.destination);

		oscillator.start(ctx.currentTime);
		oscillator.stop(ctx.currentTime + 0.3);
	};

	if (ctx.state === "suspended") {
		void ctx.resume().then(playTone).catch(() => undefined);
		return;
	}

	try {
		playTone();
	} catch {
		// Ignore autoplay or audio API errors.
	}
};
